// Søk etter kunde med store treff å trykke på. Brukes på forsiden og i «velg kunde».
// onNyKunde (valgfri): ga søket ingen treff, vises «Registrer ny kunde» med forslag fra søketeksten.
import React, { useEffect, useState } from 'react';
import Ikon from './Ikon';
import { forslagFraSok, forslagTekst } from './forslagFraSok';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function KundeSok({ id, sok, setSok, onVelg, onNyKunde, autoFocus = false }) {
  const [treff, setTreff] = useState([]);
  const [laster, setLaster] = useState(false);
  // Søket treffene hører til. «Ingen kunder funnet» vises bare når svaret gjelder det som står i feltet nå.
  const [treffFor, setTreffFor] = useState('');

  useEffect(() => {
    const q = sok.trim();
    if (q.length < 2) {
      setTreff([]);
      setTreffFor('');
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLaster(true);
      try {
        const r = await fetch(`${API_BASE_URL}/customers/search?q=${encodeURIComponent(q)}`, { signal: controller.signal });
        setTreff(r.ok ? await r.json() : []);
        setTreffFor(q);
      } catch (e) {
        if (e.name !== 'AbortError') { setTreff([]); setTreffFor(q); }
      } finally {
        setLaster(false);
      }
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [sok]);

  const harSok = sok.trim().length >= 2;
  const ferdig = harSok && !laster && treffFor === sok.trim();
  const ingenTreff = ferdig && treff.length === 0;
  const forslag = ingenTreff && onNyKunde ? forslagFraSok(sok) : null;

  return (
    <div>
      <label htmlFor={id} className="block text-base font-semibold text-slate-800 mb-1">
        Finn kunde
      </label>
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
          <Ikon navn="sok" className="w-6 h-6" />
        </span>
        <input
          id={id}
          type="search"
          inputMode="search"
          autoComplete="off"
          autoFocus={autoFocus}
          value={sok}
          onChange={(e) => setSok(e.target.value)}
          placeholder="Navn, telefon eller e-post"
          aria-describedby={`${id}-status`}
          className="w-full rounded-xl border-2 border-slate-300 bg-white py-3.5 pl-12 pr-4 text-base focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30"
        />
      </div>
      <p id={`${id}-status`} role="status" className="mt-1.5 text-sm text-slate-600 min-h-[1.25rem]">
        {!harSok ? '' : !ferdig ? 'Søker …' : ingenTreff ? 'Ingen kunder funnet.' : `${treff.length} ${treff.length === 1 ? 'kunde' : 'kunder'}`}
      </p>
      {forslag && (
        <button
          type="button"
          onClick={() => onNyKunde(forslag)}
          className="mt-1 flex w-full items-center gap-2 rounded-xl border-2 border-dashed border-sky-300 bg-white px-3 py-1.5 text-left hover:bg-sky-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-700 text-white">
            <Ikon navn="personPluss" className="w-5 h-5" />
          </span>
          <span className="min-w-0">
            <span className="block font-semibold text-sky-900">Registrer ny kunde</span>
            <span className="block text-sm text-slate-600 [overflow-wrap:anywhere]">{forslagTekst(forslag)}</span>
          </span>
        </button>
      )}
      {harSok && treff.length > 0 && (
        <ul className="mt-1 grid gap-2 sm:grid-cols-2">
          {treff.map((k) => (
            <li key={k._id}>
              <button
                type="button"
                onClick={() => onVelg(k)}
                className="flex w-full items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-left hover:border-indigo-300 hover:bg-indigo-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-800">
                  <Ikon navn="person" className="w-5 h-5" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate font-semibold text-slate-900">{[k.firstName, k.lastName].filter(Boolean).join(' ') || 'Uten navn'}</span>
                  <span className="block font-mono text-sm text-slate-600">{k.normalizedPhone || k.phoneNumber}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
